<script setup lang="ts">
// One picker per chosen platform — the replacement for `DataExportStep1.vue:37-262`, where the
// same twelve-line block was pasted eight times, once per platform, each with its own local ref,
// its own emit and its own hand-written badge row.
//
// The selection lives in `draft.selectedAccounts[platform]` and nowhere else. The only local state
// is `managerId`: which Google Ads MCC the two-stage picker is currently drilled into, a value the
// draft cannot carry because the draft holds the FINAL pick, which is the sub-account.
//
// The 8 prop watchers legacy used to resync those local refs (`Step1.vue:373-455`) are not ported.
// They were dead: the guard reads `if (!oldVal && !newVal.length && …)` and `oldVal` is always an
// array, so `!oldVal` is always `false` and no watcher body ever ran.
import { computed, ref } from 'vue'

import {
  usePlatformAccounts,
  type ExportPlatformDescriptor,
  type PlatformAccountOption,
} from '../../composables/usePlatformAccounts'
import { useDataExportStore } from '../../stores/dataExportStore'

import { useI18n } from '@/shared/i18n'
import { MultiSelect, type SelectOption } from '@/shared/ui'

type AccountId = number | string

const props = defineProps<{ descriptor: ExportPlatformDescriptor }>()
const { t } = useI18n()
const store = useDataExportStore()
const catalog = usePlatformAccounts()

const managerId = ref<AccountId | null>(null)

const accounts = computed(() => catalog.accountsFor(props.descriptor.platform))
const accountOptions = computed(() => toOptions(accounts.value))

/** The draft's account ids for this platform — the single source of truth for the selection. */
const selectedIds = computed<AccountId[]>({
  get: () => (store.draft?.selectedAccounts[props.descriptor.platform] ?? []).map((a) => a.id),
  set: (ids) => {
    const draft = store.draft
    if (!draft) return
    store.updateDraft({
      selectedAccounts: {
        ...draft.selectedAccounts,
        [props.descriptor.platform]: ids.map((id) => ({ id })),
      },
    })
  },
})

/**
 * Stage one. While a manager is drilled into it shows the MANAGER, not the draft's pick — that is
 * legacy's `selectedGoogle` vs `selectedGoogleAcc` split (`Step1.vue:314-316`), minus the two refs.
 */
const primaryIds = computed<AccountId[]>({
  get: () =>
    managerId.value !== null && selectedIds.value.length > 0
      ? [managerId.value]
      : selectedIds.value,
  set: (ids) => {
    const picked = accounts.value.find((account) => account.id === ids[0])
    managerId.value = picked?.manager === true ? picked.id : null
    selectedIds.value = ids
  },
})

/**
 * Stage two — PRESERVED (analysis §7.2, absent from the contract). A Google MCC customer has no
 * ad account of its own: without this second select, `selectMainGoogle` (`Step1.vue:346-358`)
 * leaves them holding a manager id and no exportable account.
 */
const showSubAccounts = computed(() => managerId.value !== null && selectedIds.value.length > 0)
const subAccountOptions = computed(() =>
  managerId.value === null
    ? []
    : toOptions(catalog.subAccountsOf(props.descriptor.platform, managerId.value)),
)
const subAccountIds = computed<AccountId[]>({
  get: () => selectedIds.value.filter((id) => id !== managerId.value),
  set: (ids) => {
    selectedIds.value = ids
  },
})

/** `max` is omitted rather than passed as `undefined` (`exactOptionalPropertyTypes`). */
const maxBinding = computed(() =>
  props.descriptor.maxSelection === undefined ? {} : { max: props.descriptor.maxSelection },
)

function toOptions(list: readonly PlatformAccountOption[]): SelectOption<AccountId>[] {
  return list.map((account) => ({ value: account.id, label: account.label }))
}
</script>

<template>
  <div class="platform-account-picker grid gap-sm">
    <MultiSelect
      v-model="primaryIds"
      v-bind="maxBinding"
      :options="accountOptions"
      :label="t('dataExport.source.accountLabel', { platform: t(descriptor.labelKey) })"
      :placeholder="t('dataExport.source.accountPlaceholder')"
      :state="accountOptions.length === 0 ? 'empty' : 'ready'"
      :empty-label="t('dataExport.source.accountsEmpty')"
      searchable
    />
    <MultiSelect
      v-if="showSubAccounts"
      v-model="subAccountIds"
      :max="1"
      :options="subAccountOptions"
      :label="t('dataExport.source.adsAccountLabel')"
      :placeholder="t('dataExport.source.accountPlaceholder')"
      :state="subAccountOptions.length === 0 ? 'empty' : 'ready'"
      :empty-label="t('dataExport.source.subAccountsEmpty')"
      searchable
    />
  </div>
</template>
